import React, { useState } from "react";
import { Window } from "../../components/VB";
import { Copy, Check, Smartphone, QrCode, ShoppingCart, Receipt, Printer, Wallet, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = "https://simak.fivehoster.co.id";
const BASE = `${BACKEND_URL}/api`;

/* ---------- Helper components ---------- */
function Section({ id, icon: Icon, title, children, tone = "#003399" }) {
  return (
    <section id={id} style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, borderBottom: `2px solid ${tone}`, paddingBottom: 4, marginBottom: 10 }}>
        {Icon && <Icon className="w-4 h-4" style={{ color: tone }} />}
        <h2 style={{ margin: 0, fontSize: 15, color: tone, fontWeight: 800 }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function CopyBox({ code, label = "Salin" }) {
  const [ok, setOk] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setOk(true);
      setTimeout(() => setOk(false), 1500);
      toast.success("Tersalin");
    });
  };
  return (
    <div style={{ position: "relative", marginTop: 6 }}>
      <button
        onClick={copy}
        style={{ position: "absolute", top: 6, right: 6, background: "white", border: "1px solid #999", padding: "2px 8px", fontSize: 11, cursor: "pointer", zIndex: 2, display: "inline-flex", alignItems: "center", gap: 4 }}
        data-testid="copy-code"
      >
        {ok ? <><Check className="w-3 h-3" /> OK</> : <><Copy className="w-3 h-3" /> {label}</>}
      </button>
      <pre style={{ background: "#0d1117", color: "#c9d1d9", padding: "12px 14px", paddingTop: 32, fontSize: 12, borderRadius: 4, overflowX: "auto", fontFamily: "Consolas, Monaco, monospace", margin: 0, whiteSpace: "pre" }}>
        {code}
      </pre>
    </div>
  );
}

function Endpoint({ method, path, desc, auth = "vendor" }) {
  const bg = { GET: "#dff2bf", POST: "#e3f2fd", PUT: "#fff3e0", DELETE: "#ffebee", PATCH: "#f3e5f5" }[method] || "#f5f5f5";
  const color = { GET: "#2e7d32", POST: "#1565c0", PUT: "#e65100", DELETE: "#c62828", PATCH: "#7b1fa2" }[method] || "#333";
  return (
    <div style={{ background: bg, border: `1px solid ${color}`, borderRadius: 4, padding: 8, marginBottom: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ background: "white", color, border: `1px solid ${color}`, padding: "1px 8px", fontSize: 11, fontWeight: 800, fontFamily: "Consolas, monospace", borderRadius: 3 }}>{method}</span>
        <code style={{ fontFamily: "Consolas, monospace", fontSize: 13, fontWeight: 600, color: "#222" }}>{path}</code>
        <span style={{ marginLeft: "auto", fontSize: 10, color: "#666", background: "white", padding: "1px 6px", borderRadius: 3, border: "1px solid #ccc" }}>Auth: {auth}</span>
      </div>
      {desc && <div style={{ fontSize: 12, marginTop: 4, color: "#333" }}>{desc}</div>}
    </div>
  );
}

function Param({ name, type, required, desc }) {
  return (
    <tr>
      <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontFamily: "Consolas, monospace", fontSize: 11, background: "#fafafa" }}>{name}</td>
      <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontSize: 11, color: "#666" }}>{type}</td>
      <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontSize: 11, textAlign: "center", color: required ? "#c62828" : "#888" }}>{required ? "Wajib" : "Opsi"}</td>
      <td style={{ padding: "4px 8px", border: "1px solid #ddd", fontSize: 11 }}>{desc}</td>
    </tr>
  );
}
function ParamTable({ children }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 12 }}>
      <thead>
        <tr style={{ background: "#eef4ff" }}>
          <th style={{ padding: "5px 8px", border: "1px solid #ddd", textAlign: "left", fontSize: 11 }}>Parameter</th>
          <th style={{ padding: "5px 8px", border: "1px solid #ddd", textAlign: "left", fontSize: 11 }}>Tipe</th>
          <th style={{ padding: "5px 8px", border: "1px solid #ddd", fontSize: 11 }}>Wajib</th>
          <th style={{ padding: "5px 8px", border: "1px solid #ddd", textAlign: "left", fontSize: 11 }}>Deskripsi</th>
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function Callout({ type = "info", children, title }) {
  const styles = {
    info: { bg: "#eef4ff", border: "#003399", color: "#003399", icon: "💡" },
    warn: { bg: "#fff8dc", border: "#e0c97f", color: "#8a6d00", icon: "⚠" },
    danger: { bg: "#ffd6d6", border: "#d8000c", color: "#a30000", icon: "⛔" },
    success: { bg: "#e8f7e8", border: "#4f8a10", color: "#2e5d0c", icon: "✅" },
  }[type];
  return (
    <div style={{ background: styles.bg, border: `1px solid ${styles.border}`, padding: "8px 12px", fontSize: 12, marginTop: 8, marginBottom: 8, borderLeft: `4px solid ${styles.border}` }}>
      <strong style={{ color: styles.color }}>{styles.icon} {title || type.toUpperCase()}</strong>
      <div style={{ marginTop: 4, color: "#333" }}>{children}</div>
    </div>
  );
}

/* ---------- Main page ---------- */
export default function VendorMobileApiDocs() {
  return (
    <Window title="API Docs — Mobile Vendor Koperasi">
      <div style={{ maxWidth: 1100 }}>
        {/* Sticky Base URL banner */}
        <div style={{ background: "linear-gradient(90deg,#003399,#0055cc)", color: "white", padding: 12, borderRadius: 4, marginBottom: 8 }}>
          <div style={{ fontSize: 11, opacity: 0.85 }}>Base URL Backend (production preview)</div>
          <div style={{ fontFamily: "Consolas, monospace", fontSize: 15, fontWeight: 700, letterSpacing: 0.3 }} data-testid="vm-base-url">{BASE}</div>
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.85 }}>
            Semua request kirim header: <code style={{ background: "rgba(255,255,255,0.15)", padding: "1px 6px", borderRadius: 3 }}>Authorization: Bearer &lt;access_token&gt;</code>
          </div>
        </div>

        {/* Public share link */}
        <div style={{ background: "#e8f7e8", border: "1px solid #4f8a10", padding: "8px 12px", fontSize: 12, marginBottom: 14, borderLeft: "4px solid #4f8a10", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ color: "#2e5d0c" }}>🌐 Link Publik:</strong>
          <code style={{ background: "white", padding: "3px 8px", border: "1px solid #ccc", fontSize: 12, fontFamily: "Consolas, monospace" }} data-testid="public-docs-url">
            {BACKEND_URL}/docs/vendor-mobile-api
          </code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(`${BACKEND_URL}/docs/vendor-mobile-api`).then(() => toast.success("Link publik tersalin"));
            }}
            style={{ fontSize: 11, padding: "2px 8px", cursor: "pointer" }}
            data-testid="copy-public-url"
          >
            <Copy className="w-3 h-3 inline mr-1" />Salin
          </button>
          <span style={{ color: "#555" }}>— bebas dibagikan ke tim mobile dev tanpa perlu login.</span>
        </div>

        {/* TOC */}
        <div style={{ background: "#f5f5f5", border: "1px solid #ccc", padding: 10, marginBottom: 16, fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>📑 Daftar Isi</div>
          <ol style={{ margin: 0, paddingLeft: 20, columns: 2, columnGap: 20 }}>
            <li><a href="#overview">Overview</a></li>
            <li><a href="#flow">Flow Aplikasi Mobile</a></li>
            <li><a href="#auth">Otentikasi</a></li>
            <li><a href="#vendor-me">Profil Vendor</a></li>
            <li><a href="#produk">Katalog Produk</a></li>
            <li><a href="#scan">Scan Kartu Anggota (QR/RFID)</a></li>
            <li><a href="#pos">POS — Buat Transaksi</a></li>
            <li><a href="#history">Riwayat Transaksi</a></li>
            <li><a href="#payments">Pembayaran dari Fivehoster</a></li>
            <li><a href="#printer">Konfigurasi Printer Thermal</a></li>
            <li><a href="#errors">Error Handling</a></li>
            <li><a href="#tips">Tips Implementasi Mobile</a></li>
          </ol>
        </div>

        {/* 1. Overview */}
        <Section id="overview" icon={Smartphone} title="1. Overview">
          <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            REST API ini ditujukan untuk membangun <strong>aplikasi mobile POS Koperasi</strong> yang dipakai oleh <strong>vendor / warung koperasi</strong> di kantor PT Fivehoster Karya Bangsa.
            Vendor login → scan kartu anggota (QR/RFID) → pilih produk → checkout hutang → cetak struk. Semua transaksi masuk sistem SIMAK dan otomatis dipotong dari gaji karyawan periode berikutnya.
          </p>
          <Callout type="info" title="Fitur Utama Yang Harus Ada di Mobile App">
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              <li>Login vendor (email + password)</li>
              <li>Dashboard: total transaksi bulan ini, outstanding, terbayar</li>
              <li>Scanner kamera untuk QR / input manual nomor RFID</li>
              <li>Katalog produk (list, search)</li>
              <li>Keranjang belanja (add/remove item, hitung total)</li>
              <li>Validasi plafon anggota sebelum submit</li>
              <li>Cetak struk ke printer thermal Bluetooth (58mm/80mm)</li>
              <li>Riwayat transaksi + filter bulan</li>
              <li>Rekap pembayaran yang diterima dari Fivehoster</li>
            </ul>
          </Callout>
        </Section>

        {/* 2. Flow */}
        <Section id="flow" icon={ShoppingCart} title="2. Flow Aplikasi Mobile (End-to-End)">
          <p style={{ fontSize: 13 }}>Alur transaksi standar dari buka app sampai cetak struk:</p>
          <div style={{ background: "#fafafa", border: "1px dashed #999", padding: 12, fontSize: 12, fontFamily: "Consolas, monospace", lineHeight: 1.5, whiteSpace: "pre", overflowX: "auto" }}>
{`┌──────────────────────────────────────────────────────────────────┐
│ 1. Splash → cek access_token di secure storage                   │
│     └─ ada? → skip login   |   tidak? → screen login             │
│                                                                  │
│ 2. LOGIN                                                         │
│     POST /api/auth/login  { email, password }                    │
│     → simpan access_token (expiry 12 jam)                        │
│                                                                  │
│ 3. HOME (dashboard)                                              │
│     GET /api/koperasi/vendor/me                                  │
│     → tampilkan nama warung, saldo pending, terbayar             │
│                                                                  │
│ 4. LOAD PRODUCT LIST (cache di local db)                         │
│     GET /api/koperasi/produk  → simpan ke local SQLite           │
│                                                                  │
│ 5. POS SCREEN                                                    │
│     [Scan QR] → decode string → itu adalah rfid_card             │
│     GET /api/koperasi/scan-rfid?card=<rfid>                      │
│     → tampilkan nama karyawan + sisa plafon (Rp)                 │
│                                                                  │
│     [Tambah item] → user pilih produk dari katalog               │
│                    → kalkulasi subtotal lokal (harga × qty)      │
│                                                                  │
│     [Cek plafon lokal] Jika total > sisa_plafon → cegah submit   │
│                                                                  │
│ 6. CHECKOUT                                                      │
│     POST /api/koperasi/transaksi                                 │
│       body: { rfid_card, items:[{produk_id, qty, harga}],        │
│               catatan }                                          │
│     → response berisi no_transaksi + detail lengkap              │
│                                                                  │
│ 7. CETAK STRUK                                                   │
│     Format ESC/POS 58mm/80mm (lihat section 10)                  │
│     Kirim ke printer Bluetooth (react-native-esc-pos-printer,    │
│     flutter_bluetooth_thermal, dll)                              │
│                                                                  │
│ 8. RIWAYAT                                                       │
│     GET /api/koperasi/transaksi?bulan=YYYY-MM                    │
│                                                                  │
│ 9. LAPORAN PEMBAYARAN                                            │
│     GET /api/koperasi/vendor-payments                            │
└──────────────────────────────────────────────────────────────────┘`}
          </div>

          <Callout type="warn" title="Yang Harus Diperhatikan">
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              <li>QR code yang di-scan berisi <strong>string RFID persis</strong> (contoh: <code>RFID-DEMO-001</code>). Tidak perlu parsing JSON.</li>
              <li>Backend memvalidasi plafon secara authoritative. Validasi lokal hanya untuk UX (mencegah user submit sia-sia).</li>
              <li>Semua item dalam 1 transaksi harus milik vendor yang sama (biasanya vendor yang login). Backend akan menolak jika beda.</li>
              <li>Status transaksi default <code>Terhutang</code>. Karyawan lunas otomatis ketika payroll bulan berikutnya (periode_potong).</li>
            </ul>
          </Callout>
        </Section>

        {/* 3. Auth */}
        <Section id="auth" icon={ShieldCheck} title="3. Otentikasi (JWT)">
          <Endpoint method="POST" path="/api/auth/login" desc="Login vendor. Mengembalikan access_token (Bearer, expiry 12 jam)." auth="public" />
          <CopyBox code={`curl -X POST ${BASE}/auth/login \\
  -H "Content-Type: application/json" \\
  -d '{"email":"vendor@example.com","password":"password123"}'

# Response
{
  "id": "…",
  "email": "vendor@example.com",
  "name": "Warung Demo",
  "role": "vendor",
  "access_token": "eyJhbGciOi..."
}`} />
          <Endpoint method="GET" path="/api/auth/me" desc="Cek user aktif (verifikasi token masih valid saat app dibuka)." />
          <Endpoint method="POST" path="/api/auth/change-password" desc="Ganti password sendiri." />
          <Endpoint method="POST" path="/api/auth/logout" desc="Hapus session cookie (opsional untuk mobile, cukup hapus token dari local storage)." />
          <Callout type="info" title="Penyimpanan Token">
            Simpan <code>access_token</code> di secure storage (iOS Keychain / Android EncryptedSharedPreferences).
            Kirim header <code>Authorization: Bearer &lt;token&gt;</code> di setiap request. Kalau dapat <code>401</code>, redirect ke login.
          </Callout>
        </Section>

        {/* 4. Vendor Profile */}
        <Section id="vendor-me" icon={Wallet} title="4. Profil Vendor + Statistik">
          <Endpoint method="GET" path="/api/koperasi/vendor/me" desc="Data warung + summary total transaksi (paid / pending) untuk dashboard mobile." />
          <CopyBox code={`curl ${BASE}/koperasi/vendor/me \\
  -H "Authorization: Bearer <token>"

# Response
{
  "user":   { "id":"…", "email":"…", "name":"Warung Demo" },
  "vendor": { "id":"…", "nama":"Warung Demo", "pemilik":"…", "bank_nama":"BCA",
              "bank_rekening":"…", "bank_atas_nama":"…", "status_aktif": true },
  "stats":  {
    "total_paid": 250000,     // sudah dibayar Fivehoster
    "total_pending": 45000,   // outstanding menunggu pembayaran
    "count_paid": 12,
    "count_pending": 3
  }
}`} />
        </Section>

        {/* 5. Products */}
        <Section id="produk" icon={ShoppingCart} title="5. Katalog Produk">
          <Endpoint method="GET" path="/api/koperasi/produk" desc="List semua produk milik vendor yang login (otomatis di-filter di server)." />
          <Endpoint method="POST" path="/api/koperasi/produk" desc="Tambah produk baru." />
          <Endpoint method="PUT" path="/api/koperasi/produk/{id}" desc="Update produk (harga, nama, stock aktif)." />
          <Endpoint method="DELETE" path="/api/koperasi/produk/{id}" desc="Hapus produk." />
          <CopyBox code={`# Contoh List
curl ${BASE}/koperasi/produk -H "Authorization: Bearer <token>"

# Response (array)
[
  {
    "id": "666cf2cb-…",
    "vendor_id": "d37e8bb3-…",
    "nama": "Air Mineral",
    "sku": "AIR-60",
    "harga": 4000,
    "ukuran_satuan": "600",
    "satuan": "ml",
    "deskripsi": "",
    "status_aktif": true
  }, ...
]

# Contoh Create
curl -X POST ${BASE}/koperasi/produk \\
  -H "Authorization: Bearer <token>" -H "Content-Type: application/json" \\
  -d '{"nama":"Kopi Sachet","sku":"KOP-01","harga":2500,
       "ukuran_satuan":"1","satuan":"pcs","status_aktif":true}'`} />
          <Callout type="info" title="Rekomendasi Mobile">
            Cache produk di SQLite lokal (mis. Drift/Room/Realm). Refresh saat pull-to-refresh atau setiap 30 menit.
            Ini bikin scanner responsif tanpa harus round-trip ke server untuk tiap add-item.
          </Callout>
        </Section>

        {/* 6. Scan RFID */}
        <Section id="scan" icon={QrCode} title="6. Scan Kartu Anggota (QR / RFID)">
          <Endpoint method="GET" path="/api/koperasi/scan-rfid?card={rfid}" desc="Vendor scan → server return info anggota + sisa plafon. Wajib dipanggil sebelum bikin transaksi." />
          <ParamTable>
            <Param name="card" type="query string" required desc="Nilai RFID yang di-decode dari QR (contoh: RFID-DEMO-001)" />
          </ParamTable>
          <CopyBox code={`curl "${BASE}/koperasi/scan-rfid?card=RFID-DEMO-001" \\
  -H "Authorization: Bearer <token>"

# Response
{
  "anggota_id": "bdf0ea85-…",
  "no_rekening_koperasi": "KOP-001",
  "rfid_card": "RFID-DEMO-001",
  "employee_id": "…",
  "employee_nama": "Budi Santoso",
  "no_karyawan": "EMP-001",
  "plafon": 500000,
  "terhutang": 8000,
  "sisa_plafon": 492000,
  "status_aktif": true
}

# Error 404 → kartu tidak terdaftar
# Error 400 → anggota tidak aktif`} />
          <Callout type="success" title="Format QR di Aplikasi Karyawan">
            QR code di menu <em>Karyawan → Koperasi Saya</em> berisi string RFID mentah (mis. <code>RFID-DEMO-001</code>).
            Library scanner (ML Kit / Zxing / MLK Barcode) tinggal decode dan pass ke query <code>?card=</code>.
          </Callout>
        </Section>

        {/* 7. POS */}
        <Section id="pos" icon={ShoppingCart} title="7. POS — Buat Transaksi">
          <Endpoint method="POST" path="/api/koperasi/transaksi" desc="Submit transaksi. Server validasi plafon, tulis ke DB, status default Terhutang, otomatis set periode potong = bulan berikutnya." />
          <CopyBox code={`curl -X POST ${BASE}/koperasi/transaksi \\
  -H "Authorization: Bearer <token>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "rfid_card": "RFID-DEMO-001",
    "items": [
      { "produk_id": "666cf2cb-…", "qty": 2, "harga": 4000 },
      { "produk_id": "abcd1234-…", "qty": 1, "harga": 2500 }
    ],
    "catatan": "Belanja pagi"
  }'

# Response (untuk print struk)
{
  "id": "c33f8a44-…",
  "no_transaksi": "KOP-202607-0001",
  "vendor_id": "…",
  "vendor_nama": "Warung Demo",
  "anggota_id": "…",
  "rfid_card": "RFID-DEMO-001",
  "no_rekening_koperasi": "KOP-001",
  "employee_id": "…",
  "employee_nama": "Budi Santoso",
  "no_karyawan": "EMP-001",
  "tanggal": "2026-07-01",
  "items": [
    { "produk_id":"…","nama":"Air Mineral","harga_satuan":4000,
      "ukuran_satuan":"600","satuan":"ml","qty":2,"subtotal":8000 },
    ...
  ],
  "total": 10500,
  "plafon_sebelum_sisa": 492000,
  "plafon_sesudah_sisa": 481500,
  "periode_potong": "2026-08",
  "status": "Terhutang",
  "catatan": "Belanja pagi",
  "created_at": "2026-07-01T09:37:00Z"
}`} />
          <ParamTable>
            <Param name="rfid_card" type="string" required desc="Nomor RFID hasil scan" />
            <Param name="items" type="array" required desc="Minimal 1 item. Setiap item: produk_id (uuid), qty (int, >0), harga (float, opsional; server override dgn harga terbaru)" />
            <Param name="catatan" type="string" required={false} desc="Catatan bebas, muncul di struk & riwayat" />
          </ParamTable>
          <Callout type="danger" title="Validasi Server">
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              <li>Jika <code>total &gt; sisa_plafon</code> → HTTP 400 <em>"Plafon tidak cukup. Sisa: Rp X. Total transaksi: Rp Y"</em></li>
              <li>Jika produk beda vendor → HTTP 400 <em>"Semua item harus dari vendor yang sama"</em></li>
              <li>Jika anggota tidak aktif → HTTP 400</li>
              <li>Jika RFID tidak ada → HTTP 404</li>
            </ul>
          </Callout>
        </Section>

        {/* 8. History */}
        <Section id="history" icon={Receipt} title="8. Riwayat Transaksi">
          <Endpoint method="GET" path="/api/koperasi/transaksi?bulan=YYYY-MM&status=Terhutang" desc="List transaksi vendor. Auto-filter by vendor_id dari JWT. Optional filter bulan/status." />
          <ParamTable>
            <Param name="bulan" type="YYYY-MM" required={false} desc="Filter bulan transaksi (default: semua)" />
            <Param name="status" type="string" required={false} desc="Terhutang | Lunas" />
            <Param name="anggota_id" type="uuid" required={false} desc="Admin-only: filter per anggota" />
          </ParamTable>
          <CopyBox code={`curl "${BASE}/koperasi/transaksi?bulan=2026-07" \\
  -H "Authorization: Bearer <token>"

# Response (array, urut created_at desc)
[
  { "id":"…","no_transaksi":"KOP-202607-0002","total":15500,
    "employee_nama":"Budi","status":"Terhutang", ... },
  ...
]`} />
        </Section>

        {/* 9. Payments received */}
        <Section id="payments" icon={Wallet} title="9. Rekap Pembayaran Diterima">
          <Endpoint method="GET" path="/api/koperasi/vendor-payments?vendor_id={id}" desc="List pembayaran yang sudah dibayar oleh Fivehoster ke vendor (rekap per bulan). Auto-filter untuk vendor login." auth="admin+vendor" />
          <CopyBox code={`curl ${BASE}/koperasi/vendor-payments \\
  -H "Authorization: Bearer <token>"

# Response
[
  {
    "id": "…",
    "vendor_id": "…",
    "vendor_nama": "Warung Demo",
    "periode": "2026-06",
    "payment_date": "2026-07-05",
    "nominal": 245000,
    "metode": "Transfer",
    "catatan": ""
  }, ...
]`} />
          <Callout type="info" title="Alur Pembayaran">
            Setiap awal bulan, admin Fivehoster mengumpulkan rekap transaksi hutang karyawan → potong dari payroll → lalu bayar akumulasi ke vendor.
            Setelah pembayaran, semua transaksi bulan tsb ter-mark <code>vendor_payment_status: "Lunas"</code>.
          </Callout>
        </Section>

        {/* 10. Printer */}
        <Section id="printer" icon={Printer} title="10. Konfigurasi Printer Thermal">
          <Endpoint method="GET" path="/api/koperasi/vendor-printer-config" desc="Ambil konfigurasi printer tersimpan (paper width, header/footer, cut, drawer)." />
          <Endpoint method="PUT" path="/api/koperasi/vendor-printer-config" desc="Simpan preferensi printer per vendor." />
          <CopyBox code={`# Response default
{
  "paper_width": "58mm",          // atau "80mm"
  "connection_type": "bluetooth", // atau "usb"/"network"
  "device_name": "",              // MAC address / device id
  "auto_print": true,
  "header_lines": [],
  "footer_lines": ["Terima kasih!", "Simpan struk ini sebagai bukti."],
  "cut_paper": true,
  "open_drawer": false
}`} />
          <Callout type="info" title="Contoh Layout Struk 58mm (32 char/line)">
            <pre style={{ margin: "6px 0 0 0", fontFamily: "Consolas, monospace", fontSize: 11, background: "white", padding: 8, border: "1px dashed #999", whiteSpace: "pre" }}>
{`      WARUNG DEMO
   Kantin Koperasi PT Fivehoster
================================
No  : KOP-202607-0001
Tgl : 01/07/2026 09:37
Kasir: Warung Demo
Anggota:
  Budi Santoso (EMP-001)
  KOP-001 / RFID-DEMO-001
--------------------------------
Air Mineral (600ml)
   2 x  4.000       8.000
Kopi Sachet
   1 x  2.500       2.500
--------------------------------
TOTAL              10.500
Metode      : HUTANG
Sisa Plafon : 481.500
Potong Gaji : 2026-08
================================
    Terima kasih!
Simpan struk sebagai bukti.`}
            </pre>
          </Callout>
          <Callout type="warn" title="Library Cetak Rekomendasi">
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              <li><strong>React Native</strong>: <code>react-native-esc-pos-printer</code>, <code>react-native-bluetooth-escpos-printer</code></li>
              <li><strong>Flutter</strong>: <code>flutter_bluetooth_thermal</code>, <code>esc_pos_bluetooth</code></li>
              <li><strong>Native Android</strong>: ESCPOS-ThermalPrinter-Android (library)</li>
              <li><strong>iOS</strong>: ESCPos-iOS-SDK (Star / Epson / generic)</li>
            </ul>
          </Callout>
        </Section>

        {/* 11. Errors */}
        <Section id="errors" icon={AlertTriangle} title="11. Error Handling" tone="#c62828">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#ffebee" }}>
                <th style={{ border: "1px solid #ccc", padding: "5px 8px", textAlign: "left" }}>HTTP</th>
                <th style={{ border: "1px solid #ccc", padding: "5px 8px", textAlign: "left" }}>Kapan</th>
                <th style={{ border: "1px solid #ccc", padding: "5px 8px", textAlign: "left" }}>Aksi di Mobile</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ border: "1px solid #eee", padding: 5 }}>400</td><td style={{ border: "1px solid #eee", padding: 5 }}>Validasi bisnis gagal (plafon, produk vendor beda, dsb)</td><td style={{ border: "1px solid #eee", padding: 5 }}>Tampilkan pesan <code>detail</code> dari response ke toast/snackbar</td></tr>
              <tr><td style={{ border: "1px solid #eee", padding: 5 }}>401</td><td style={{ border: "1px solid #eee", padding: 5 }}>Token expired / invalid</td><td style={{ border: "1px solid #eee", padding: 5 }}>Hapus token, redirect ke login</td></tr>
              <tr><td style={{ border: "1px solid #eee", padding: 5 }}>403</td><td style={{ border: "1px solid #eee", padding: 5 }}>Role bukan vendor / vendor_id belum tertaut</td><td style={{ border: "1px solid #eee", padding: 5 }}>Tampilkan "Akses ditolak" → logout</td></tr>
              <tr><td style={{ border: "1px solid #eee", padding: 5 }}>404</td><td style={{ border: "1px solid #eee", padding: 5 }}>Kartu RFID tidak ditemukan / produk tidak ada</td><td style={{ border: "1px solid #eee", padding: 5 }}>Tampilkan alert modal, minta rescan</td></tr>
              <tr><td style={{ border: "1px solid #eee", padding: 5 }}>500</td><td style={{ border: "1px solid #eee", padding: 5 }}>Server error</td><td style={{ border: "1px solid #eee", padding: 5 }}>Retry 1x → jika masih error, simpan ke offline queue</td></tr>
            </tbody>
          </table>
          <Callout type="info" title="Format Error Response">
            <CopyBox code={`{ "detail": "Plafon tidak cukup. Sisa: Rp 5.000. Total transaksi: Rp 15.500" }`} />
          </Callout>
        </Section>

        {/* 12. Tips */}
        <Section id="tips" title="12. Tips Implementasi Mobile" icon={Smartphone}>
          <ul style={{ fontSize: 12.5, lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
            <li><strong>Offline Queue</strong>: Simpan transaksi ke SQLite lokal saat submit gagal (no signal). Sinkron otomatis saat online kembali (POST ulang <code>/koperasi/transaksi</code>). Beri penanda "pending sync" di UI.</li>
            <li><strong>Kamera Scanner</strong>: Gunakan Google ML Kit Barcode / Zxing untuk QR. Timeout scan 30 detik → fallback ke input manual.</li>
            <li><strong>Input Manual RFID</strong>: Sediakan tombol "Input Manual" untuk kasus kartu rusak. Field numeric/alphanumeric.</li>
            <li><strong>Format Rupiah</strong>: Gunakan <code>Intl.NumberFormat('id-ID')</code> atau equivalent. Backend selalu return angka mentah.</li>
            <li><strong>Konfirmasi Ganda</strong>: Sebelum POST transaksi &gt; Rp 50.000, tampilkan modal konfirmasi dengan preview total + nama karyawan.</li>
            <li><strong>Cetak Ulang</strong>: Simpan JSON response transaksi di local db → user bisa reprint struk kalau ada masalah cetak.</li>
            <li><strong>Bluetooth Reconnect</strong>: Simpan device MAC address printer di preferences. Auto-connect saat POS screen dibuka.</li>
            <li><strong>Sinkronisasi Katalog</strong>: Poll <code>/koperasi/produk</code> setiap 30 menit atau saat pull-to-refresh.</li>
            <li><strong>Handle 401 secara Global</strong>: Pasang interceptor HTTP (Axios/Dio) yang redirect ke Login screen otomatis.</li>
            <li><strong>Uji Plafon Habis</strong>: Test flow ketika sisa plafon 0 → tombol Bayar harus disabled + tampilkan pesan.</li>
          </ul>
        </Section>

        {/* Credentials */}
        <Section id="creds" title="Kredensial Demo" icon={ShieldCheck} tone="#4f8a10">
          <div style={{ background: "#e8f7e8", border: "1px solid #4f8a10", padding: 12, fontSize: 13 }}>
            <div><strong>Vendor Demo</strong></div>
            <div>Email: <code>vendor@example.com</code></div>
            <div>Password: <code>password123</code></div>
            <div style={{ marginTop: 6 }}><strong>Karyawan Demo (untuk uji QR)</strong></div>
            <div>Email: <code>karyawan@fivehoster.co.id</code></div>
            <div>Password: <code>karyawan123</code></div>
            <div>RFID Card: <code>RFID-DEMO-001</code></div>
            <div>Plafon: <code>Rp 500.000</code></div>
          </div>
        </Section>
      </div>
    </Window>
  );
}
